import Cookies from 'js-cookie';

import { validateForUI } from 'common/helpers/jsonValidator';
import { isValidPhoneNumber } from 'common/helpers/phoneHelper';

const { isEmail, isISO8601 } = require('validator');

const properties = {
  firstName: { type: 'string' },
  lastName: { type: 'string' },
  email: { type: 'string' },
  phone: { type: 'string' },
  countryCode: {
    type: 'string',
    maxLength: 2,
    minLength: 2,
    pattern: '[A-Z][A-Z]'
  },
  emailMarketingStatus: {
    type: 'string',
    enum: ['Subscribed', 'Unsubscribed']
  },
  metaData: {
    type: 'object'
  },
  questions: {
    type: 'object'
  }
};

const resultCreateSchema = {
  type: 'object',
  properties,
  additionalProperties: false
};

export const validateSubmit = (result, form, customerMetaData, settings) =>
  validateForUI(
    resultCreateSchema,
    result,
    customValidate(result, form, customerMetaData, settings)
  );

const customValidate = (result, form, customerMetaData, settings) => {
  const errorArray = [];
  // Email is required if its coming from public
  if (!Cookies.get(`customerToken`)) {
    if (!result.email) {
      const error = {
        field: `email`,
        message: 'Required'
      };
      errorArray.push(error);
    }
    if (result.email && !isEmail(result.email)) {
      const error = {
        field: `email`,
        message: 'Invalid Email'
      };
      errorArray.push(error);
    }
    if (
      result.phone &&
      !isValidPhoneNumber(result.phone, result.countryCode, settings)
    ) {
      errorArray.push({
        field: 'phone',
        message: 'Invalid phone number for your country code'
      });
    }
    const customerAttributesArray = [
      'firstName',
      'lastName',
      'countryCode',
      'phone',
      'emailMarketingStatus'
    ];
    customerAttributesArray.forEach((attribute) => {
      if (form.customerAttributes[attribute]) {
        if (
          form.customerAttributes[attribute] === 'required' &&
          !result[attribute]
        ) {
          const error = {
            field: attribute,
            message: 'Required'
          };
          errorArray.push(error);
        }
      } else if (result[attribute]) {
        const error = {
          field: attribute,
          message: 'Invalid additional attribute'
        };
        errorArray.push(error);
      }
    });
  }

  // Then we have meta data
  if (form.metaDataAttributes && form.metaDataAttributes.length > 0) {
    const metaDataErrorArray = validateMetaDataForForm(
      result.metaData,
      form.metaDataAttributes,
      customerMetaData
    );
    errorArray.push(...metaDataErrorArray);
  }

  // Then we have general questions
  // Are all the required fields answered

  if (form.questions && form.questions.length > 0) {
    const questionErrorArray = validateQuestions(form, result);
    errorArray.push(...questionErrorArray);
  }

  return errorArray;
};

const validateMetaDataForForm = (metaData, codeArray, customerMetaData) => {
  const metaDataIdArray = Object.keys(customerMetaData.data);
  const metaDataArray = metaDataIdArray.map((id) => customerMetaData.data[id]);
  const filteredList = metaDataArray.filter((metaConfig) => {
    if (codeArray.includes(metaConfig.code)) {
      return true;
    }
    return false;
  });

  const list = {
    customerMetaData: filteredList
  };

  return validate(list, metaData);
};

const validate = (list, metaData) => {
  const errorArray = [];
  list.customerMetaData.forEach((config) => {
    if (config.isRequired) {
      if (!metaData || typeof metaData[config.code] === 'undefined') {
        errorArray.push({
          field: `metaData-${config.id}`,
          message: `required`
        });
      }
    }

    if (metaData && metaData[config.code]) {
      if (config.dataType === 'Radio' || config.dataType === 'Select') {
        if (
          config.options.length > 0 &&
          metaData[config.code] &&
          !config.options.find((option) => option === metaData[config.code])
        ) {
          errorArray.push({
            field: `metaData-${config.id}`,
            message: `should be one of ${config.options
              .reduce((acc, val) => `${val}${acc},`, '')
              .slice(0, -1)}`
          });
        }
      }
    }

    if (metaData && metaData[config.code]) {
      const value = metaData[config.code];
      if (value) {
        switch (config.dataType) {
          case 'Integer': {
            if (!isInteger(value)) {
              errorArray.push({
                field: `metaData-${config.id}`,
                message: `must be an integer`
              });
            }
            break;
          }
          case 'Number': {
            if (!isFloat(value)) {
              errorArray.push({
                field: `metaData-${config.id}`,
                message: `must be a number`
              });
            }
            break;
          }
          case 'Date Picker': {
            if (!isISO8601(value)) {
              errorArray.push({
                field: `metaData-${config.id}`,
                message: `must be a date`
              });
            }
            break;
          }
          case 'Yes / No':
          case 'True / False': {
            if (typeof value !== 'boolean') {
              errorArray.push({
                field: `metaData-${config.id}`,
                message: `must be a boolean`
              });
            }
            break;
          }
          case 'Radio':
          case 'Checkbox':
          case 'Select':
          case 'String':
          case 'TextArea':
            break;
          default:
            throw new Error('Invalid type');
        }
      }
    }
  });

  return errorArray;
};

const validateQuestions = (form, result) => {
  const errorArray = [];
  if (form.questions) {
    form.questions.forEach((question) => {
      if (question.isRequired) {
        if (
          !result.questions ||
          typeof result.questions[question.code] === 'undefined'
        ) {
          errorArray.push({
            field: `question-${question.id}`,
            message: `required`
          });
        }
      }

      if (result.questions && result.questions[question.code]) {
        if (question.dataType === 'Radio' || question.dataType === 'Select') {
          if (
            question.options.length > 0 &&
            !question.options.find(
              (option) => option === result.questions[question.code]
            )
          ) {
            errorArray.push({
              field: `question-${question.id}`,
              message: `should be one of ${question.options
                .reduce((acc, val) => `${val}${acc},`, '')
                .slice(0, -1)}`
            });
          }
        }
      }

      if (result.questions && result.questions[question.code]) {
        const value = result.questions[question.code];
        if (value) {
          switch (question.dataType) {
            case 'Integer': {
              if (!isInteger(value)) {
                errorArray.push({
                  field: `question-${question.id}`,
                  message: `must be an integer`
                });
              }
              break;
            }
            case 'Number': {
              if (!isFloat(value)) {
                errorArray.push({
                  field: `question-${question.id}`,
                  message: `must be a number`
                });
              }
              break;
            }
            case 'Date Picker': {
              if (!isISO8601(value)) {
                errorArray.push({
                  field: `question-${question.id}`,
                  message: `must be a date`
                });
              }
              break;
            }
            case 'Yes / No':
            case 'True / False': {
              if (typeof value !== 'boolean') {
                errorArray.push({
                  field: `question-${question.id}`,
                  message: `must be a boolean`
                });
              }
              break;
            }
            case 'Radio':
            case 'Checkbox':
            case 'Select':
            case 'String':
            case 'TextArea':
              break;
            default:
              throw new Error('Invalid type');
          }
        }
      }
    });
  }

  return errorArray;
};

const isFloat = (n) => typeof n === 'number';

const isInteger = (n) =>
  typeof n === 'number' && n === +n && Math.round(n) === n;
